(() => {
  const selector = '.UiProductCarousel[data-flickity-config]';
  const managed = new WeakSet();
  const instances = new Map();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const groupedLayout = window.matchMedia('(min-width: 641px)');
  let sequence = 0;

  class CarouselNavigation {
    constructor(element, template) {
      this.element = element;
      this.navigation = template.content.firstElementChild.cloneNode(true);
      this.previous = this.navigation.querySelector('[data-carousel-previous]');
      this.next = this.navigation.querySelector('[data-carousel-next]');
      this.status = this.navigation.querySelector('[data-carousel-status]');
      this.fill = this.navigation.querySelector('[data-carousel-fill]');
      this.schedule = this.schedule.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerEnd = this.onPointerEnd.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onDragStart = event => { if (this.drag) event.preventDefault(); };
      this.onPrevious = () => this.select(this.index - 1);
      this.onNext = () => this.select(this.index + 1);
      this.index = 0;
      this.count = 1;
      this.frame = null;

      if (!element.id) element.id = `UiProductCarousel-${++sequence}`;
      if (!element.hasAttribute('tabindex')) element.tabIndex = 0;
      if (!element.hasAttribute('role')) element.setAttribute('role', 'group');
      if (!element.hasAttribute('aria-label')) {
        const heading = element.closest('section')?.querySelector('.SectionHeader__Heading');
        element.setAttribute('aria-label', heading?.textContent.trim() || template.dataset.label);
      }
      this.previous.setAttribute('aria-controls', element.id);
      this.next.setAttribute('aria-controls', element.id);
      this.previous.addEventListener('click', this.onPrevious);
      this.next.addEventListener('click', this.onNext);
      element.addEventListener('scroll', this.schedule, { passive: true });
      element.addEventListener('keydown', this.onKeydown, true);
      element.addEventListener('pointerdown', this.onPointerDown);
      element.addEventListener('pointermove', this.onPointerMove);
      ['pointerup', 'pointercancel', 'lostpointercapture'].forEach(type =>
        element.addEventListener(type, this.onPointerEnd));
      element.addEventListener('dragstart', this.onDragStart);
      element.addEventListener('click', this.onClick, true);
      element.after(this.navigation);
      element.dataset.uiCarouselReady = '';
      this.resizeObserver = new ResizeObserver(this.schedule);
      this.resizeObserver.observe(element);
      this.classObserver = new MutationObserver(this.schedule);
      this.classObserver.observe(element, { attributes: true, attributeFilter: ['class'] });
      this.update();
    }

    bindFlickity() {
      const flickity = window.Flickity?.data(this.element);
      if (flickity === this.flickity) return;
      this.unbindFlickity();
      this.flickity = flickity;
      if (flickity) {
        ['select', 'resize', 'cellChange', 'settle'].forEach(event => flickity.on(event, this.schedule));
      }
    }

    unbindFlickity() {
      if (!this.flickity) return;
      ['select', 'resize', 'cellChange', 'settle'].forEach(event => this.flickity.off(event, this.schedule));
    }

    schedule() {
      if (this.frame !== null) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        if (this.element.isConnected) this.update();
      });
    }

    update() {
      this.bindFlickity();
      if (this.flickity && this.element.hasAttribute('data-ui-carousel-auto-init')) {
        const options = JSON.parse(this.element.dataset.flickityConfig || '{}');
        const group = groupedLayout.matches ? (options.groupCells || true) : 1;
        const width = this.element.getBoundingClientRect().width;
        if (this.flickity.options.groupCells !== group || this.lastWidth !== width) {
          this.lastWidth = width;
          this.flickity.options.groupCells = group;
          this.flickity.resize();
        }
      }
      const active = Boolean(this.flickity?.isActive);
      const cells = active ? this.flickity.cells.map(cell => cell.element)
        : [...this.element.children].filter(cell => cell.classList.contains('Carousel__Cell'));
      const visible = this.element.getBoundingClientRect().width > 0;
      if (active) {
        this.slideIndices = [];
        this.flickity.slides.forEach((slide, index) => {
          const previous = this.flickity.slides[this.slideIndices.at(-1)];
          if (!previous || Math.abs(slide.target - previous.target) > 1) this.slideIndices.push(index);
        });
        this.count = this.slideIndices.length;
        const selectedTarget = this.flickity.slides[this.flickity.selectedIndex]?.target;
        this.index = Math.max(0, this.slideIndices.findIndex(index =>
          Math.abs(this.flickity.slides[index].target - selectedTarget) <= 1));
      } else {
        const bounds = this.element.getBoundingClientRect();
        const maximum = Math.max(0, this.element.scrollWidth - this.element.clientWidth);
        const grouped = window.matchMedia('(min-width: 641px)').matches;
        const firstWidth = cells[0]?.getBoundingClientRect().width || this.element.clientWidth;
        const groupSize = grouped ? Math.max(1, Math.floor(this.element.clientWidth / firstWidth)) : 1;
        this.positions = [];
        cells.forEach((cell, cellIndex) => {
          if (cellIndex % groupSize !== 0) return;
          const box = cell.getBoundingClientRect();
          let position = Math.max(0, Math.min(maximum,
            box.left - bounds.left + this.element.scrollLeft - (grouped ? 0 : (this.element.clientWidth - box.width) / 2)));
          if (cellIndex === 0) position = 0;
          if (!this.positions.length || Math.abs(position - this.positions.at(-1)) > 1) {
            this.positions.push(position);
          }
        });
        if (cells.length && maximum > 0 && maximum - this.positions.at(-1) > 1) {
          this.positions.push(maximum);
        }
        this.count = this.positions.length;
        this.index = this.positions.reduce((nearest, position, index) =>
          Math.abs(position - this.element.scrollLeft) < Math.abs(this.positions[nearest] - this.element.scrollLeft)
            ? index : nearest, 0);
      }
      this.count = Math.max(1, this.count);
      this.index = Math.max(0, Math.min(this.count - 1, this.index));
      this.navigation.hidden = !this.flickity || !visible || !cells.length || this.count <= 1;
      this.element.toggleAttribute('data-ui-carousel-draggable', visible && this.count > 1);
      this.previous.disabled = this.index === 0;
      this.next.disabled = this.index === this.count - 1;
      this.setText(this.status, this.navigation.dataset.stepFormat
        .replace('__CURRENT__', this.index + 1).replace('__TOTAL__', this.count));
      this.fill.style.transform = `scaleX(${(this.index + 1) / this.count})`;
      this.navigation.dataset.current = this.index + 1;
      this.navigation.dataset.total = this.count;
      this.navigation.dataset.mode = active ? 'flickity' : 'native';
    }

    setText(element, value) {
      if (element.textContent !== value) element.textContent = value;
    }

    select(index) {
      this.update();
      index = Math.max(0, Math.min(this.count - 1, index));
      if (this.flickity?.isActive) {
        this.flickity.select(this.slideIndices[index], false, reducedMotion.matches);
      } else if (this.positions.length) {
        this.element.scrollTo({ left: this.positions[index], behavior: reducedMotion.matches ? 'auto' : 'smooth' });
      }
      this.schedule();
    }

    onPointerDown(event) {
      this.suppressClick = false;
      if (event.pointerType !== 'mouse' || event.button !== 0 || !event.isPrimary) return;
      this.update();
      if (!this.flickity || this.flickity.isActive || this.count <= 1) return;
      if (event.target.closest('button, input, select, textarea, label, [role="button"]')) return;
      this.drag = { id: event.pointerId, x: event.clientX, y: event.clientY,
        scroll: this.element.scrollLeft, moved: false };
    }

    onPointerMove(event) {
      const drag = this.drag;
      if (!drag || event.pointerId !== drag.id) return;
      if (!event.buttons || this.flickity?.isActive) {
        this.endDrag(false);
        return;
      }
      const distance = event.clientX - drag.x;
      if (!drag.moved) {
        if (Math.abs(event.clientY - drag.y) > Math.abs(distance)) {
          this.endDrag(false);
          return;
        }
        if (Math.abs(distance) < (this.flickity.options.dragThreshold || 8)) return;
        drag.moved = true;
        this.element.setPointerCapture(drag.id);
        this.element.dataset.uiCarouselDragging = '';
      }
      event.preventDefault();
      this.element.scrollLeft = drag.scroll - distance;
    }

    onPointerEnd(event) {
      if (this.drag?.id === event.pointerId) this.endDrag(event.type === 'pointerup');
    }

    endDrag(snap) {
      const drag = this.drag;
      if (!drag) return;
      this.drag = null;
      delete this.element.dataset.uiCarouselDragging;
      if (this.element.hasPointerCapture(drag.id)) this.element.releasePointerCapture(drag.id);
      if (drag.moved) {
        this.suppressClick = true;
        if (snap && this.element.isConnected) {
          this.update();
          this.select(this.index);
        }
      }
    }

    onClick(event) {
      if (!this.suppressClick || event.detail === 0) return;
      this.suppressClick = false;
      event.preventDefault();
      event.stopImmediatePropagation();
    }

    onKeydown(event) {
      if (event.target !== this.element) return;
      const targets = { ArrowLeft: this.index - 1, ArrowRight: this.index + 1, Home: 0, End: this.count - 1 };
      if (!(event.key in targets)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.select(targets[event.key]);
    }

    destroy() {
      this.endDrag(false);
      this.unbindFlickity();
      this.resizeObserver.disconnect();
      this.classObserver.disconnect();
      this.element.removeEventListener('scroll', this.schedule);
      this.element.removeEventListener('keydown', this.onKeydown, true);
      this.element.removeEventListener('pointerdown', this.onPointerDown);
      this.element.removeEventListener('pointermove', this.onPointerMove);
      ['pointerup', 'pointercancel', 'lostpointercapture'].forEach(type =>
        this.element.removeEventListener(type, this.onPointerEnd));
      this.element.removeEventListener('dragstart', this.onDragStart);
      this.element.removeEventListener('click', this.onClick, true);
      delete this.element.dataset.uiCarouselDraggable;
      this.previous.removeEventListener('click', this.onPrevious);
      this.next.removeEventListener('click', this.onNext);
      if (this.frame !== null) cancelAnimationFrame(this.frame);
      this.navigation.remove();
      if (managed.has(this.element)) this.flickity?.destroy();
      delete this.element.dataset.uiCarouselReady;
    }
  }

  const initialise = element => {
    if (typeof window.Flickity !== 'function' || window.Flickity.data(element)) return;
    const options = JSON.parse(element.dataset.flickityConfig || '{}');
    new window.Flickity(element, {
      ...options, wrapAround: false, contain: true, cellAlign: 'left',
      prevNextButtons: false, pageDots: false,
      groupCells: groupedLayout.matches ? (options.groupCells || true) : 1,
    });
    managed.add(element);
    window.initProductCardMedia?.(element);
  };

  const mountCart = container => {
    const track = container.querySelector('.cart__recomendations__track');
    if (!track || !track.querySelector('.cart__recomendations__slide')) return;
    container.classList.add('UiProductCarouselHost--cart');
    track.classList.add('UiProductCarousel', 'UiProductCarousel--cart');
    track.dataset.flickityConfig = JSON.stringify({
      cellSelector: '.cart__recomendations__slide', groupCells: 1,
      watchCSS: false, dragThreshold: 8, accessibility: true,
    });
    track.setAttribute('data-ui-carousel-auto-init', '');
    const existing = window.Flickity?.data(track);
    if (existing) {
      const added = [...track.children].filter(node => node.classList.contains('cart__recomendations__slide'));
      if (added.length) existing.append(added);
      existing.resize();
    } else initialise(track);
    scan();
  };
  window.UiProductCarousel = { mountCart };

  const scan = () => {
    const template = document.getElementById('UiCarouselPagination');
    if (!template) return;
    instances.forEach((instance, element) => {
      if (!element.isConnected) {
        instance.destroy();
        instances.delete(element);
      }
    });
    document.querySelectorAll(selector).forEach(element => {
      if (element.hasAttribute('data-ui-carousel-auto-init') && !window.Flickity?.data(element)) {
        initialise(element);
      }
      // Existing controllers may create Flickity before new cards are observed.
      window.initProductCardMedia?.(element);
      if (!instances.has(element)) instances.set(element, new CarouselNavigation(element, template));
      else instances.get(element).schedule();
    });
  };

  const start = () => {
    scan();
    groupedLayout.addEventListener('change', () => {
      instances.forEach(instance => instance.update());
    });
    const observer = new MutationObserver(records => {
      if (records.some(record => [...record.addedNodes, ...record.removedNodes]
        .some(node => node.nodeType === Node.ELEMENT_NODE))) scan();
    });
    observer.observe(document.body, { childList: true, subtree: true });
    document.addEventListener('shopify:section:load', scan);
    document.addEventListener('ui:carousel:ready', scan);
    document.addEventListener('click', event => {
      const button = event.target.closest('[data-carousel-demo-state]');
      if (!button) return;
      const element = button.closest('[data-carousel-demo]').querySelector(selector);
      const instance = instances.get(element);
      if (!instance) return;
      const positions = { first: 0, middle: Math.floor((instance.count - 1) / 2), last: instance.count - 1 };
      instance.select(positions[button.dataset.carouselDemoState]);
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
